import { Grid, Typography, Button, Chip, LinearProgress, Box } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Progress } from '@backstage/core-components';
import { useDependencies } from '../hooks/useDependencies';
import { getSlugFromEntity, getManifestPathsFromEntity } from '../utils/annotations';
import { DependencySummary } from './DependencySummary';
import { DependencyTable } from './DependencyTable';
import { Ecosystem } from '../utils/types';

const ecosystemLabel: Record<Ecosystem, string> = {
  node: 'Node.js',
  php: 'PHP',
  java: 'Java',
  python: 'Python',
  go: 'Go',
};

export const DependencyViewerPage = () => {
  const { entity } = useEntity();
  const slug = getSlugFromEntity(entity);
  const manifestPaths = getManifestPathsFromEntity(entity);
  const { data, loading, error, retry } = useDependencies(slug, manifestPaths);

  if (loading) return <Progress />;

  if (error) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="error">
            Erro ao carregar dependências: {error.message}
          </Typography>
          <Button
            variant="outlined"
            onClick={retry}
            style={{ marginTop: 8 }}
          >
            Tentar novamente
          </Button>
        </Grid>
      </Grid>
    );
  }

  if (!data) return null;

  const progressPercent = data.dependencies.length > 0
    ? Math.round((data.resolvedCount / data.dependencies.length) * 100)
    : 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {data.manifests.map((m, i) => {
          const label = m.path
            ? `${ecosystemLabel[m.ecosystem] ?? m.ecosystem} — ${m.path}/${m.filename}`
            : `${ecosystemLabel[m.ecosystem] ?? m.ecosystem} — ${m.filename}`;
          return (
            <Chip
              key={i}
              label={label}
              color="primary"
              variant="outlined"
              size="small"
              style={{ marginRight: 8, marginBottom: 4 }}
            />
          );
        })}
      </Grid>

      {data.resolving && (
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Box flexGrow={1}>
              <LinearProgress variant="determinate" value={progressPercent} />
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
              Verificando versões… {data.resolvedCount}/{data.dependencies.length} ({progressPercent}%)
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <DependencySummary summary={data.summary} />
      </Grid>
      <Grid item xs={12}>
        <DependencyTable dependencies={data.dependencies} showSource={data.manifests.length > 1} resolving={data.resolving} />
      </Grid>
    </Grid>
  );
};
